import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCortes } from '../../hooks/useCortes';
import { liquidacionAPI } from '../../services/api';
import { formatCurrency, formatCurrencyInt } from '../../utils/formatters';
import { nombreMes } from '../../utils/periodos';
import { PROVEEDORES, armarLiquidacion, areaCorta, areaClase, conclusiones } from '../../utils/liquidacion';
import { generarInformePDF, generarInformeExcel } from '../../utils/liquidacionReportes';

const pesos = (n) => (n === null || n === undefined ? '—' : formatCurrencyInt(n));
const pct = (v, t) => (t ? `${((v / t) * 100).toFixed(1)}%` : '—');

/** Barra Mesa / Sitio con su valor y porcentaje (el color siempre va con la etiqueta). */
const BarraAreas = ({ mesa, sitio, sinArea }) => {
  const total = mesa + sitio + sinArea;
  if (!total) return null;
  const p = (v) => (v / total) * 100;
  return (
    <div className="liq-barra">
      <div className="liq-barra-track" role="img" aria-label={`Mesa ${p(mesa).toFixed(1)}%, Sitio ${p(sitio).toFixed(1)}%`}>
        <span className="liq-seg liq-seg-mesa" style={{ width: `${p(mesa)}%` }} />
        <span className="liq-seg liq-seg-sitio" style={{ width: `${p(sitio)}%` }} />
        {sinArea > 0 && <span className="liq-seg liq-seg-sin" style={{ width: `${p(sinArea)}%` }} />}
      </div>
      <div className="liq-barra-leyenda">
        <span><i className="liq-dot liq-seg-mesa" />Mesa de Servicio · {p(mesa).toFixed(1)}%</span>
        <span><i className="liq-dot liq-seg-sitio" />Soporte en Sitio · {p(sitio).toFixed(1)}%</span>
        {sinArea > 0 && <span><i className="liq-dot liq-seg-sin" />Sin área · {p(sinArea).toFixed(1)}%</span>}
      </div>
    </div>
  );
};

/** TRM del mes, editable aquí mismo cuando falta. */
const TrmCaptura = ({ periodo, onGuardar }) => {
  const [val, setVal] = useState('');
  const [guardando, setGuardando] = useState(false);
  const ok = parseFloat(val) > 0;
  const guardar = async () => {
    if (!ok) return;
    setGuardando(true);
    await onGuardar(parseFloat(val));
    setGuardando(false);
  };
  return (
    <div className="liq-aviso liq-aviso-trm">
      <div>
        <strong>Falta la TRM de {nombreMes(periodo)}.</strong> Sin ella el costo Cloud no se puede pasar a
        pesos y no entra en los totales.
      </div>
      <div className="liq-trm-form">
        <input
          className="proceso-input"
          type="number"
          min="0"
          step="0.01"
          placeholder="TRM USD → COP"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
        />
        <button type="button" className="btn-primary consumo-cargar-btn" disabled={!ok || guardando} onClick={guardar}>
          {guardando ? 'Guardando…' : 'Guardar TRM'}
        </button>
      </div>
    </div>
  );
};

/** TRM del mes en la barra: se ve siempre y se edita con el lápiz. */
const TrmEditable = ({ trm, onGuardar }) => {
  const [editando, setEditando] = useState(false);
  const [val, setVal] = useState('');
  const [guardando, setGuardando] = useState(false);
  const ok = parseFloat(val) > 0;
  const abrir = () => { setVal(trm ? String(trm) : ''); setEditando(true); };
  const guardar = async () => {
    if (!ok) return;
    setGuardando(true);
    await onGuardar(parseFloat(val));
    setGuardando(false);
    setEditando(false);
  };
  if (!editando) {
    return (
      <button type="button" className="liq-trm-chip" onClick={abrir} title="Editar la TRM del mes">
        <span className="liq-trm-chip-label">TRM</span>
        <strong>{trm ? `$${trm.toLocaleString('es-CO')}` : 'Sin TRM'}</strong>
        <svg width="14" height="14" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
          <path d="M2 26h28v2H2zM25.4 9c.8-.8.8-2 0-2.8l-3.6-3.6c-.8-.8-2-.8-2.8 0l-15 15V24h6.4l15-15zm-5-5L24 7.6l-3 3L17.4 7l3-3zM6 22v-3.6l10-10 3.6 3.6-10 10H6z" />
        </svg>
      </button>
    );
  }
  return (
    <div className="liq-trm-edit">
      <span className="liq-trm-chip-label">TRM</span>
      <input
        autoFocus
        type="number"
        min="0"
        step="0.01"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') guardar(); if (e.key === 'Escape') setEditando(false); }}
        aria-label="TRM del mes"
      />
      <button type="button" className="liq-trm-ok" disabled={!ok || guardando} onClick={guardar}>
        {guardando ? 'Guardando…' : 'Guardar'}
      </button>
      <button type="button" className="liq-trm-cancel" onClick={() => setEditando(false)} disabled={guardando}>Cancelar</button>
    </div>
  );
};

/**
 * Módulo Liquidación (2026-10). Para un mes cruza el costo Cloud (CSV del mes
 * distribuido por aplicación) con el costo del proveedor (tarifa del contrato)
 * y dice cuánto paga cada cliente, separado en Mesa de Servicio y Soporte en
 * Sitio. Es la vista para la gerencia: el detalle de cada cliente va por
 * aplicación (Cloud y proveedor juntos) y se exporta como informe PDF y Excel.
 */
export const LiquidacionModule = ({ showNotification }) => {
  const { cortes, loadCortes, setTrm } = useCortes(showNotification);
  const [periodo, setPeriodo] = useState('');
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('clientes');
  const [abiertos, setAbiertos] = useState({});
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    loadCortes().then((lista) => {
      if (lista.length) setPeriodo((p) => p || lista[lista.length - 1].periodo);
    });
  }, [loadCortes]);

  const cargar = useCallback(async (p) => {
    if (!p) return;
    setLoading(true);
    try {
      const res = await liquidacionAPI.get(p);
      setDatos(res.data);
    } catch (error) {
      console.error('Error cargando liquidación:', error);
      showNotification?.('error', 'Liquidación', error.response?.data?.error || 'No se pudo calcular la liquidación');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    cargar(periodo);
    setAbiertos({});
  }, [periodo, cargar]);

  const liq = useMemo(() => (datos?.periodo === periodo ? armarLiquidacion(datos) : null), [datos, periodo]);
  const idx = cortes.findIndex((c) => c.periodo === periodo);
  const t = liq?.totales;
  const sinTrm = liq && !liq.trm;
  const pend = liq?.pendientes;
  const q = busqueda.trim().toLowerCase();
  const clientes = liq ? liq.clientes.filter((c) => !q || c.name.toLowerCase().includes(q)) : [];
  const apps = liq ? liq.apps.filter((a) => !q || a.name.toLowerCase().includes(q)) : [];
  const todosAbiertos = clientes.length > 0 && clientes.every((c) => abiertos[c.id]);
  const avisar = { onSuccess: (a, b) => showNotification?.('success', a, b), onError: (a, b) => showNotification?.('error', a, b) };

  const guardarTrm = async (n) => {
    if (await setTrm(datos.corte.id, n)) {
      showNotification?.('success', 'TRM', `TRM de ${nombreMes(periodo)}: ${n}`);
      cargar(periodo);
    }
  };

  const hayPendientes = pend && (pend.cloud_sin_regla_usd > 0.005 || pend.apps_sin_clientes.length > 0 ||
    pend.sin_tarifa.some((x) => x.contrato) || pend.proveedores_sin_contrato.length > 0);

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Liquidación</h1>
        <p>Cuánto paga cada cliente en el mes: costo Cloud + costo del proveedor, separado en Mesa de Servicio y Soporte en Sitio</p>
      </div>

      {cortes.length === 0 ? (
        <div className="consumo-vacio">
          <h2>No hay meses cargados</h2>
          <p>Carga el CSV del mes en Costos Cloud → Consumo mensual para calcular la liquidación.</p>
        </div>
      ) : (
        <>
          <div className="consumo-toolbar">
            <div className="consumo-nav">
              <button type="button" className="consumo-nav-btn" disabled={idx <= 0} onClick={() => setPeriodo(cortes[idx - 1].periodo)} aria-label="Mes anterior">‹</button>
              <select className="consumo-nav-select" value={periodo} onChange={(e) => setPeriodo(e.target.value)} aria-label="Mes">
                {cortes.map((c) => <option key={c.periodo} value={c.periodo}>{nombreMes(c.periodo)}</option>)}
              </select>
              <button type="button" className="consumo-nav-btn" disabled={idx === -1 || idx >= cortes.length - 1} onClick={() => setPeriodo(cortes[idx + 1].periodo)} aria-label="Mes siguiente">›</button>
            </div>
            {liq && datos?.corte && <TrmEditable trm={liq.trm} onGuardar={guardarTrm} />}
            {liq && (
              <span className="consumo-meta">
                Contratos: {liq.contratos.map((c) => PROVEEDORES[c.proveedor]).join(', ') || 'ninguno'}
              </span>
            )}
            <div className="consumo-toolbar-acciones">
              <button
                type="button"
                className="liq-btn-sec"
                disabled={!liq}
                onClick={() => generarInformeExcel({ liq, nombreMes: nombreMes(periodo), ...avisar })}
              >
                Excel
              </button>
              <button
                type="button"
                className="btn-primary consumo-cargar-btn"
                disabled={!liq}
                onClick={() => generarInformePDF({ liq, nombreMes: nombreMes(periodo), ...avisar })}
              >
                Informe PDF
              </button>
            </div>
          </div>

          {loading && !liq && <p className="proceso-hint">Calculando…</p>}

          {liq && (
            <>
              {pend.sin_trm && <TrmCaptura periodo={periodo} onGuardar={guardarTrm} />}
              {hayPendientes && (
                <div className="liq-aviso">
                  <strong>Quedan costos sin cobrar a ningún cliente:</strong>
                  <ul>
                    {pend.cloud_sin_regla_usd > 0.005 && (
                      <li>{formatCurrency(pend.cloud_sin_regla_usd)} de Cloud sin asignar a una aplicación (Costos Cloud → Distribución servicios).</li>
                    )}
                    {pend.apps_sin_clientes.map((a) => (
                      <li key={a.name}>{a.name}: {formatCurrency(a.cloud_usd)} de Cloud, pero la aplicación no tiene clientes marcados.</li>
                    ))}
                    {pend.proveedores_sin_contrato.map((p) => (
                      <li key={p}>{PROVEEDORES[p]} no tiene contrato vigente en {nombreMes(periodo)} (Costos Proveedor).</li>
                    ))}
                    {pend.sin_tarifa.filter((x) => x.contrato).map((x) => (
                      <li key={`${x.proveedor}${x.name}`}>{x.name} ({PROVEEDORES[x.proveedor]}) no tiene tarifa en el contrato.</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Cifras clave: total y su reparto por área */}
              <div className="liq-hero">
                <div className="liq-hero-total">
                  <span className="liq-hero-label">Costo total · {nombreMes(periodo)}</span>
                  <span className="liq-hero-valor">{pesos(t.total)}</span>
                  <span className="liq-hero-sub">{liq.clientes.length} clientes{sinTrm && ' · sin Cloud (falta TRM)'}</span>
                </div>
                <div className="liq-hero-area liq-kpi-mesa">
                  <span className="liq-hero-label"><i className="liq-dot liq-seg-mesa" />Mesa de Servicio</span>
                  <span className="liq-hero-valor2">{pesos(t.mesa)}</span>
                  <span className="liq-hero-sub">{pct(t.mesa, t.total)} del total</span>
                </div>
                <div className="liq-hero-area liq-kpi-sitio">
                  <span className="liq-hero-label"><i className="liq-dot liq-seg-sitio" />Soporte en Sitio</span>
                  <span className="liq-hero-valor2">{pesos(t.sitio)}</span>
                  <span className="liq-hero-sub">{pct(t.sitio, t.total)} del total</span>
                </div>
              </div>
              <BarraAreas mesa={t.mesa} sitio={t.sitio} sinArea={t.sinArea} />

              {/* De dónde sale el costo + puntos clave */}
              <div className="liq-dos">
                <section className="liq-panel">
                  <h3>De dónde sale el costo</h3>
                  <table className="liq-comp">
                    <tbody>
                      <tr>
                        <td><span className="liq-tipo liq-tipo-cloud">Cloud</span>IBM Cloud</td>
                        <td className="num">{sinTrm ? `${formatCurrency(t.cloud_usd)} USD` : pesos(t.cloud)}</td>
                        <td className="num liq-comp-pct">{sinTrm ? '—' : pct(t.cloud, t.total)}</td>
                      </tr>
                      {Object.keys(PROVEEDORES).filter((p) => liq.porProveedor[p]).map((p) => (
                        <tr key={p}>
                          <td><span className={`liq-tipo liq-tipo-${p}`}>{PROVEEDORES[p]}</span>Proveedor</td>
                          <td className="num">{pesos(liq.porProveedor[p])}</td>
                          <td className="num liq-comp-pct">{pct(liq.porProveedor[p], t.total)}</td>
                        </tr>
                      ))}
                      <tr className="liq-comp-total">
                        <td>Total</td>
                        <td className="num">{pesos(t.total)}</td>
                        <td className="num liq-comp-pct">100%</td>
                      </tr>
                    </tbody>
                  </table>
                </section>
                <section className="liq-panel">
                  <h3>Puntos clave</h3>
                  <ul className="liq-puntos">
                    {conclusiones(liq, pesos).map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </section>
              </div>

              <div className="consumo-tabs">
                <button type="button" className={`consumo-tab ${vista === 'clientes' ? 'activa' : ''}`} onClick={() => setVista('clientes')}>
                  Por cliente <span className="consumo-cuenta">{liq.clientes.length}</span>
                </button>
                <button type="button" className={`consumo-tab ${vista === 'apps' ? 'activa' : ''}`} onClick={() => setVista('apps')}>
                  Por aplicación <span className="consumo-cuenta">{liq.apps.length}</span>
                </button>
                {vista === 'clientes' && (
                  <button
                    type="button"
                    className="contrato-link-btn liq-expandir"
                    onClick={() => setAbiertos(todosAbiertos ? {} : Object.fromEntries(clientes.map((c) => [c.id, true])))}
                  >
                    {todosAbiertos ? 'Contraer todos' : 'Expandir todos'}
                  </button>
                )}
                <input
                  className="proceso-input consumo-buscar"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder={vista === 'clientes' ? 'Buscar cliente' : 'Buscar aplicación'}
                />
              </div>

              {vista === 'clientes' ? (
                <div className="consumo-tabla-wrap">
                  <table className="consumo-tabla liq-tabla">
                    <thead>
                      <tr>
                        <th>Cliente / aplicación</th>
                        <th className="num">Cloud</th>
                        <th className="num">Proveedor</th>
                        <th className="num">Total</th>
                        <th className="num liq-col-mesa">Mesa</th>
                        <th className="num liq-col-sitio">Sitio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientes.length === 0 && <tr><td colSpan={6} className="consumo-tabla-vacia">Nada que mostrar</td></tr>}
                      {clientes.map((c) => {
                        const abierto = abiertos[c.id];
                        return (
                          <React.Fragment key={c.id}>
                            <tr className="consumo-fila-servicio liq-fila-cliente" onClick={() => setAbiertos((a) => ({ ...a, [c.id]: !a[c.id] }))}>
                              <td>
                                <span className={`consumo-chevron ${abierto ? 'abierto' : ''}`}>›</span>
                                {c.name}
                                <span className="liq-pct-cliente">{pct(c.total, t.total)}</span>
                              </td>
                              <td className="num">{sinTrm ? <span className="consumo-ayuda">{formatCurrency(c.cloud_usd)} USD</span> : pesos(c.cloud)}</td>
                              <td className="num">{pesos(c.proveedor)}</td>
                              <td className="num liq-total">{pesos(c.total)}</td>
                              <td className="num liq-col-mesa">{pesos(c.mesa)}</td>
                              <td className="num liq-col-sitio">{pesos(c.sitio)}</td>
                            </tr>
                            {abierto && c.apps.map((a) => (
                              <tr key={a.key} className="liq-fila-app">
                                <td>
                                  <div className="liq-app-nombre">
                                    {a.name}
                                    <span className={`liq-area liq-area-${areaClase(a.frac)}`}>{areaCorta(a.frac)}</span>
                                  </div>
                                  <div className="liq-app-nota">
                                    {[a.notaProveedor, a.notaCloud].filter(Boolean).join(' · ')}
                                  </div>
                                </td>
                                <td className="num">
                                  {a.tieneCloud ? (sinTrm ? <span className="consumo-ayuda">{formatCurrency(a.cloud_usd)} USD</span> : pesos(a.cloud)) : <span className="liq-vacio">—</span>}
                                </td>
                                <td className="num">
                                  {a.proveedor ? (
                                    <>
                                      {pesos(a.proveedor)}
                                      <span className="liq-origen">{a.origen}</span>
                                    </>
                                  ) : <span className="liq-vacio">—</span>}
                                </td>
                                <td className="num liq-subtotal">{pesos(a.total)}</td>
                                <td className="num liq-col-mesa">{a.mesa ? pesos(a.mesa) : <span className="liq-vacio">—</span>}</td>
                                <td className="num liq-col-sitio">{a.sitio ? pesos(a.sitio) : <span className="liq-vacio">—</span>}</td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    {!q && (
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td className="num">{sinTrm ? `${formatCurrency(t.cloud_usd)} USD` : pesos(t.cloud)}</td>
                          <td className="num">{pesos(t.proveedor)}</td>
                          <td className="num">{pesos(t.total)}</td>
                          <td className="num liq-col-mesa">{pesos(t.mesa)}</td>
                          <td className="num liq-col-sitio">{pesos(t.sitio)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              ) : (
                <div className="consumo-tabla-wrap">
                  <table className="consumo-tabla liq-tabla">
                    <thead>
                      <tr>
                        <th>Aplicación</th>
                        <th>Área</th>
                        <th className="num">Clientes</th>
                        <th className="num">Cloud</th>
                        <th className="num">Proveedor</th>
                        <th className="num">Total</th>
                        <th className="num">% del total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {apps.map((a) => (
                        <tr key={a.key}>
                          <td title={a.herramientas.join(' · ')}>
                            <div className="liq-app-nombre">{a.name}</div>
                            <div className="liq-app-nota">{a.proveedores.map((p) => PROVEEDORES[p]).join(', ')} · {a.herramientas.join(', ')}</div>
                          </td>
                          <td><span className={`liq-area liq-area-${areaClase(a.frac_mesa)}`}>{areaCorta(a.frac_mesa)}</span></td>
                          <td className="num">{a.clientes}</td>
                          <td className="num">{sinTrm ? <span className="consumo-ayuda">{formatCurrency(a.cloud_usd)} USD</span> : pesos(a.cloud)}</td>
                          <td className="num">{pesos(a.proveedor)}</td>
                          <td className="num liq-total">{pesos(a.total)}</td>
                          <td className="num liq-comp-pct">{pct(a.total, t.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="consumo-ayuda">
                    Cuánto vale tener cada aplicación en el mes: su consumo Cloud más lo que se cobra de proveedor a sus clientes.
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
